import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { API } from "@/config/api";
import { AuthProvider, useAuth } from "@/config/AuthContext";

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('No session ID found');
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        const userData = response.data.user;
        login(userData);
        toast.success('Successfully logged in!');
        
        if (!userData.membership_tier) {
          navigate('/onboarding');
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed');
        navigate('/login');
      }
    };

    processSession();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route Component - uses shared auth context
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // Only redirect if auth check is complete and user is not authenticated
    if (isAuthenticated === false) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isAuthenticated ? children : null;
};

// Import pages
import Landing from "@/pages/Landing";
import Landing_v2 from "@/pages/Landing_v2";
import Landing_v3 from "@/pages/Landing_v3";
import Login from "@/pages/Login";
import Login_v2 from "@/pages/Login_v2";
import Signup from "@/pages/Signup";
import Signup_v2 from "@/pages/Signup_v2";
import Onboarding from "@/pages/Onboarding";
import Onboarding_v2 from "@/pages/Onboarding_v2";
import SelectTier from "@/pages/SelectTier";
import SelectTier_v2 from "@/pages/SelectTier_v2";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutSuccess_v2 from "@/pages/CheckoutSuccess_v2";
// V1 pages (fallback)
import Dashboard from "@/pages/Dashboard";
import RequestHelp from "@/pages/RequestHelp";
import Billing from "@/pages/Billing";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminMembers from "@/pages/AdminMembers";
import AdminRequests from "@/pages/AdminRequests";
import AdminCovers from "@/pages/AdminCovers";
// V2 pages (default)
import Dashboard_v2 from "@/pages/Dashboard_v2";
import RequestHelp_v2 from "@/pages/RequestHelp_v2";
import Billing_v2 from "@/pages/Billing_v2";
import AdminDashboard_v2 from "@/pages/AdminDashboard_v2";
import AdminMembers_v2 from "@/pages/AdminMembers_v2";
import AdminRequests_v2 from "@/pages/AdminRequests_v2";
import AdminCovers_v2 from "@/pages/AdminCovers_v2";

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* V2 as Default */}
      <Route path="/" element={<Landing_v2 />} />
      <Route path="/login" element={<Login_v2 />} />
      <Route path="/signup" element={<Signup_v2 />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding_v2 /></ProtectedRoute>} />
      <Route path="/select-tier" element={<ProtectedRoute><SelectTier_v2 /></ProtectedRoute>} />
      <Route path="/checkout-success" element={<ProtectedRoute><CheckoutSuccess_v2 /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard_v2 /></ProtectedRoute>} />
      <Route path="/request-help" element={<ProtectedRoute><RequestHelp_v2 /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing_v2 /></ProtectedRoute>} />
      
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard_v2 /></ProtectedRoute>} />
      <Route path="/admin/members" element={<ProtectedRoute><AdminMembers_v2 /></ProtectedRoute>} />
      <Route path="/admin/requests" element={<ProtectedRoute><AdminRequests_v2 /></ProtectedRoute>} />
      <Route path="/admin/covers" element={<ProtectedRoute><AdminCovers_v2 /></ProtectedRoute>} />
      
      {/* V1 Backup Routes */}
      <Route path="/v1" element={<Landing />} />
      <Route path="/v1/login" element={<Login />} />
      <Route path="/v1/signup" element={<Signup />} />
      <Route path="/v1/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/v1/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/v1/request-help" element={<ProtectedRoute><RequestHelp /></ProtectedRoute>} />
      <Route path="/v1/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/v1/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/v1/admin/members" element={<ProtectedRoute><AdminMembers /></ProtectedRoute>} />
      <Route path="/v1/admin/requests" element={<ProtectedRoute><AdminRequests /></ProtectedRoute>} />
      <Route path="/v1/admin/covers" element={<ProtectedRoute><AdminCovers /></ProtectedRoute>} />
      <Route path="/v1/select-tier" element={<ProtectedRoute><SelectTier /></ProtectedRoute>} />
      
      <Route path="/v3" element={<Landing_v3 />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
